import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { Subscription, finalize, take } from 'rxjs';
import { UniqueIdService } from '../../../services/unique-id/unique-id.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { UserService } from '../../../services/user/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../loading/loading.service';
import { ToastService } from '../../../services/toast/toast.service';
import { customEmailValidator } from '../../../shared/validators/email-validator';
import { ProfileService } from 'src/app/modules/services/profile/profile.service';
import { EntitiesService } from 'src/app/modules/services/entities/entities.service';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent implements OnInit {
  @Input() isEditMode: boolean = false;
  isMaster: boolean = false;
  hide = true;
  public id: string;
  subsctiptions: Subscription[] = [];
  horizontalPosition: MatSnackBarHorizontalPosition = 'end';
  verticalPosition: MatSnackBarVerticalPosition = 'top';
  form: FormGroup;
  userId: string | null = null;

  nome: any = '';
  email: any = '';
  finalizouCadastro: boolean = false;

  allPerfilUsuario: any[] = [];
  perfilUsuarioInput: string = '';
  selectPerfilUsuarioId: any = null;
  disabledTooltipMessage: string = 'Ação não permitida';
  entities: any[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private uniqueIdService: UniqueIdService,
    private fb: FormBuilder,
    private userService: UserService,
    private profileService: ProfileService,
    private router: Router,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private entitiesService: EntitiesService,
    private route: ActivatedRoute
  ) {
    this.id = this.uniqueIdService.generateUniqueIdWithPrefix('login');
    this.form = this.fb.group({
      nome: [null, [Validators.required, Validators.minLength(3)]],
      email: [null, [Validators.required, customEmailValidator()]],
      perfilUsuarioFilter: ['',Validators.required],
      perfilUsuarioFilterInput: [''],
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.userId = params.get('id');
      if (this.userId) {
        this.loadUserData(this.userId);
      }
    });

    this.loadProfiles();
    this.getEntitieTypes();
  }

  loadUserData(userId: string) {
    this.loadingService.showLoading();

    this.subsctiptions.push(
      this.userService
        .getUser(userId)
        .pipe(
          take(1),
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(
          (response: any) => {
            const perfilUsuarioFilter = response.profileUser
              ? response.profileUser.id
              : '';
            const perfilUsuarioFilterInput = response.profileUser
              ? response.profileUser.nome
              : '';

            this.form.patchValue({
              nome: response.nome,
              email: response.email,
              perfilUsuarioFilter,
              perfilUsuarioFilterInput,
            });

            this.selectPerfilUsuarioId = perfilUsuarioFilter;
            this.perfilUsuarioInput = perfilUsuarioFilterInput;
            this.isMaster = response?.master;
            if (response?.master) {
              this.form.disable();
            }
            if (response.finalizouCadastro) {
              this.form.get('nome')?.disable();
              this.form.get('email')?.disable();
              this.nome = response.nome;
              this.email = response.email;
              this.finalizouCadastro = response.finalizouCadastro;
            }
          },
          (erro) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError();
            }
          }
        )
    );
  }

  loadProfiles() {
    this.loadingService.showLoading();

    this.subsctiptions.push(
      this.profileService
        .getProfiles()
        .pipe(
          take(1),
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(
          (response: any) => {
            this.allPerfilUsuario = response.content;
          },
          (erro) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError();
            }
          }
        )
    );
  }

  getEntitieTypes() {
    this.subsctiptions.push(
      this.entitiesService
        .getEntitiesTypes()
        .pipe(take(1))
        .subscribe(
          (resp: any) => {
            this.entities = resp;
          },
          (erro) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError();
            }
          }
        )
    );
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  onSubmit() {
    this.loadingService.showLoading();

    this.isEditMode ? this.handleEditSubmit() : this.handleCreateSubmit();
  }

  handleCreateSubmit() {
    const form = this.formatFormData();

    this.subsctiptions.push(
      this.userService
        .createUser(form)
        .pipe(
          take(1),
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(
          (response) => {
            this.toastService.showSuccess('Usuário cadastrado com sucesso.');
            this.goToListUser();
          },
          (erro) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError();
            }
          }
        )
    );
  }

  handleEditSubmit() {
    const form = this.formatFormData();

    this.subsctiptions.push(
      this.userService
        .editUser(this.userId, form)
        .pipe(
          take(1),
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(
          (response) => {
            this.toastService.showSuccess('Usuário editado com sucesso.');
            this.goToListUser();
          },
          (erro) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError();
            }
          }
        )
    );
  }

  formatFormData() {
    const formValues = this.form.value;
    return {
      nome: this.finalizouCadastro ? this.nome : formValues.nome,
      email: this.finalizouCadastro ? this.email : formValues.email,
      profileUserId: formValues.perfilUsuarioFilter,
    };
  }

  onSelectPerfilUsuario(value: any) {
    this.setFormValue(value, 'perfilUsuarioFilter');
    this.selectPerfilUsuarioId = value;
  }

  onSelectPerfilUsuarioInput(value: any) {
    this.setFormValue(value, 'perfilUsuarioFilterInput');
  }

  setFormValue(value: any, formInput: any) {
    if (value) {
      return this.form.patchValue({ [formInput]: value });
    }
    return this.form.patchValue({ [formInput]: '' });
  }

  goToListUser() {
    this.router.navigate(['/usuarios']);
  }
}
